import { useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router';
import { ApiError } from '../api/client';
import { AuthLayout } from '../components/AuthLayout';
import { Alert, Field } from '../components/ui';
import { useAuth } from '../context/AuthContext';
import { SIDETITLER, useSidehode } from '../lib/sidehode';

/**
 * Hvor man havner etter innlogging.
 *
 * Står ett sted fordi den brukes to: både når skjemaet sendes, og når man
 * kommer til en allerede innlogget økt. Sto den bare i skjemaet, ville
 * omdirigeringen under vunnet kappløpet og sendt alle til /app – også de som
 * skulle fått velge bryllup.
 */
function etterInnlogging(antallBryllup: number): string {
  if (antallBryllup === 0) return '/kom-i-gang';
  if (antallBryllup > 1) return '/velg-bryllup';
  return '/app';
}

/** Godtar bare stier innenfor vår egen side. */
function trygtNeste(verdi: string | null): string | null {
  if (!verdi) return null;
  if (!verdi.startsWith('/') || verdi.startsWith('//')) return null;
  return verdi;
}

export function LoginPage() {
  const { login, status, weddings } = useAuth();
  useSidehode(SIDETITLER.loggInn, '/logg-inn');
  const navigate = useNavigate();
  // Satt av tilbakestillingssiden. Uten kvitteringen her er det ikke tydelig at
  // det nye passordet faktisk ble lagret.
  const [params] = useSearchParams();
  const passordEndret = params.get('passord') === 'endret';
  /**
   * Hvor man skal etterpå, når man ble sendt hit fra et sted.
   *
   * Bare interne stier godtas. Uten den sjekken kunne en lenke med
   * `?neste=https://…` sendt en nyinnlogget bruker til en fremmed side som
   * ser ut som vår – som er akkurat det en åpen omdirigering brukes til.
   */
  const neste = trygtNeste(params.get('neste'));
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (status === 'authenticated') {
    return <Navigate to={neste ?? etterInnlogging(weddings.length)} replace />;
  }

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const okt = await login(email, password);
      // Kom man hit fra en invitasjonslenke, skal man tilbake dit – ikke inn i
      // appen med beskjed om å finne e-posten igjen.
      if (neste) {
        navigate(neste, { replace: true });
        return;
      }
      // Har man flere bryllup – sitt eget, og ett man er toastmaster i – skal
      // man få velge hvilket man går inn i, ikke lande i det vi tilfeldigvis
      // husket sist.
      navigate(etterInnlogging(okt.weddings.length));
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : 'Klarte ikke å logge inn. Prøv igjen.',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout title="Velkommen tilbake" subtitle="Logg inn for å fortsette planleggingen.">
      <form onSubmit={onSubmit} noValidate>
        <Alert>{error}</Alert>
        {passordEndret && !error ? (
          <Alert kind="success">Passordet er endret. Logg inn med det nye.</Alert>
        ) : null}

        <Field label="E-post">
          <input
            className="input"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </Field>

        <Field label="Passord">
          <input
            className="input"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </Field>

        <button className="btn btn-primary btn-block btn-lg" type="submit" disabled={busy}>
          {busy ? 'Logger inn …' : 'Logg inn'}
        </button>

        <p className="small muted" style={{ marginTop: '1.25rem' }}>
          <Link to="/glemt-passord">Glemt passordet?</Link>
        </p>
        <p className="small muted">
          Har dere ikke konto? <Link to="/registrer">Opprett en gratis</Link>
        </p>
      </form>
    </AuthLayout>
  );
}
